import { useCallback, useContext } from "react";
import { ScreenSize, UI } from "@wwimmo/ui";
import * as Sentry from "@sentry/react";
import { observer } from "mobx-react";
import { RootStoreContext } from "src/stores/RootStore";
import { ErpType } from "src/network/User";
import { IAccountingReadOnly } from "src/stores/krediflow/InvoiceTypes";
import { getDateString } from "src/stores/krediflow/Utils";
import { useTranslation } from "react-i18next";
import { formatAccountingAmountString } from "src/stores/krediflow/Utils";
import { VatProportionHint } from "src/screens/creditors/creditor/details/accountings/accounting/VatProportionHint";

const InvoiceAccoutingsReadOnlyViewBase = () => {
    const { t } = useTranslation();

    const {
        invoiceStore,
        authStore,
        uiStore,
        accountingAccountSearchStore,
        accountingCostcenterStore,
        accountingVatStore
    } = useContext(RootStoreContext);

    const renderListRowIT2 = useCallback(
        (accounting: IAccountingReadOnly, screenSize: ScreenSize) => {
            let listRows: React.ReactElement[] = [];

            let account = "";
            if (accounting.account) {
                account = accountingAccountSearchStore.getFormattedAccountFieldValue(accounting.account);
            }

            let costCenter = "-";
            if (accounting.costCenter) {
                costCenter = accountingCostcenterStore.getFormattedCostcenterFieldValue(accounting.costCenter);
            }

            let bookingText = <></>;

            if (accounting.text) {
                const accountingTextParts = accounting.text.split("\n");

                bookingText = (
                    <div>
                        {accountingTextParts.map((accountingText, index) => (
                            <div key={index}>{accountingText}</div>
                        ))}
                    </div>
                );
            }

            const amount = formatAccountingAmountString(accounting.amount);

            // #24071: the deviating input-tax share belongs to the amount it applies to.
            const amountCell = accounting.vatProportion ? (
                <>
                    <div>{amount}</div>
                    <div className="small">{`${t("screens.kredi_flow.accountings.vat_proportion")} ${accounting.vatProportion}`}</div>
                </>
            ) : (
                amount
            );

            const totalAmount = formatAccountingAmountString(accounting.totalAmount);

            switch (screenSize) {
                case ScreenSize.DESKTOP:
                    if (accounting.isTotalRow) {
                        listRows = [
                            <UI.List.Row key={"row-1"}>
                                <UI.List.Cell
                                    key={"c-4"}
                                    colspan={11}
                                    value={t("screens.kredi_flow.total")}
                                    className="text-right bold"
                                />
                                <UI.List.Cell key={"c-5"} colspan={1} value={totalAmount} className="text-right bold" />
                            </UI.List.Row>
                        ];
                    } else {
                        listRows = [
                            <UI.List.Row key={"row-1"}>
                                <UI.List.Cell key={"c-1"} colspan={3} value={account} />
                                <UI.List.Cell key={"c-2"} colspan={3} value={costCenter} />
                                <UI.List.Cell key={"c-3"} colspan={4} value={bookingText} />
                                <UI.List.Cell key={"c-4"} colspan={2} value={amountCell} className="text-right" />
                            </UI.List.Row>
                        ];
                    }
                    break;
                case ScreenSize.MOBILE:
                    if (accounting.isTotalRow) {
                        listRows = [
                            <UI.List.Row key={"row-1"}>
                                <UI.List.Cell
                                    key={"c-4"}
                                    colspan={6}
                                    value={t("screens.kredi_flow.total")}
                                    className="text-right bold"
                                />
                                <UI.List.Cell key={"c-4"} colspan={6} value={totalAmount} className="text-right bold" />
                            </UI.List.Row>
                        ];
                    } else {
                        listRows = [
                            <UI.List.Row key={"row-1"}>
                                <UI.List.Cell key={"c-1"} colspan={12} value={account} />
                            </UI.List.Row>,
                            <UI.List.Row key={"row-2"}>
                                <UI.List.Cell key={"c-2"} colspan={12} value={costCenter} />
                            </UI.List.Row>,
                            <UI.List.Row key={"row-3"}>
                                <UI.List.Cell key={"c-3"} colspan={6} value={bookingText} />
                            </UI.List.Row>,
                            <UI.List.Row key={"row-4"}>
                                <UI.List.Cell key={"c-4"} colspan={12} value={amountCell} className="text-right" />
                            </UI.List.Row>
                        ];
                    }
                    break;
            }

            return <UI.List.Item key={`item-${accounting.id}`} screenSize={screenSize} rows={listRows} />;
        },
        [accountingAccountSearchStore, accountingCostcenterStore, t]
    );

    const renderListHeaderIT2 = useCallback(
        (screenSize: ScreenSize) => {
            if (screenSize === ScreenSize.MOBILE) {
                return <></>;
            }

            const rows = [
                <UI.List.Cell key={"c-1"} colspan={3} value={t("screens.kredi_flow.accountings.account")} />,
                <UI.List.Cell key={"c-2"} colspan={3} value={t("screens.kredi_flow.accountings.costcenter")} />,
                <UI.List.Cell key={"c-3"} colspan={4} value={t("screens.kredi_flow.accountings.text")} />,
                <UI.List.Cell
                    key={"c-4"}
                    colspan={2}
                    value={t("screens.kredi_flow.accountings.amount")}
                    className="text-right"
                />
            ];
            return <UI.List.Header rows={rows} />;
        },
        [t]
    );

    const renderListRowRimo = useCallback(
        (accounting: IAccountingReadOnly, screenSize: ScreenSize) => {
            let listRows: React.ReactElement[] = [];

            let realestate = <></>;
            if (accounting.realestate) {
                realestate = (
                    <>
                        <div>{accounting.realestate.name}</div>
                        <div>{`${accounting.realestate.zip} ${accounting.realestate.city}`}</div>
                    </>
                );
            }

            let account = "";
            if (accounting.account) {
                account = accountingAccountSearchStore.getFormattedAccountFieldValue(accounting.account);
            }

            let bookingText = <></>;

            if (accounting.text) {
                const accountingTextParts = accounting.text.split("\n");

                bookingText = (
                    <div>
                        {accountingTextParts.map((accountingText, index) => (
                            <div key={index}>{accountingText}</div>
                        ))}
                    </div>
                );
            }

            const amount = formatAccountingAmountString(accounting.amount);

            // #24071: the deviating input-tax share belongs to the amount it applies to.
            const amountCell = accounting.vatProportion ? (
                <>
                    <div>{amount}</div>
                    <div className="small">{`${t("screens.kredi_flow.accountings.vat_proportion")} ${accounting.vatProportion}`}</div>
                </>
            ) : (
                amount
            );

            const totalAmount = formatAccountingAmountString(accounting.totalAmount);

            switch (screenSize) {
                case ScreenSize.DESKTOP:
                    if (accounting.isTotalRow) {
                        listRows = [
                            <UI.List.Row key={"row-2"}>
                                <UI.List.Cell
                                    key={"c-5"}
                                    colspan={11}
                                    value={t("screens.kredi_flow.total")}
                                    className="text-right bold"
                                />
                                <UI.List.Cell key={"c-6"} colspan={1} value={totalAmount} className="text-right bold" />
                            </UI.List.Row>
                        ];
                    } else {
                        listRows = [
                            <UI.List.Row key={"row-1"}>
                                <UI.List.Cell key={"c-1"} colspan={3} value={realestate} />
                                <UI.List.Cell key={"c-2"} colspan={3} value={account} />
                                <UI.List.Cell key={"c-3"} colspan={4} value={bookingText} />
                                <UI.List.Cell key={"c-4"} colspan={2} value={amountCell} className="text-right" />
                            </UI.List.Row>
                        ];
                    }
                    break;
                case ScreenSize.MOBILE:
                    if (accounting.isTotalRow) {
                        listRows = [
                            <UI.List.Row key={"row-2"}>
                                <UI.List.Cell
                                    key={"c-5"}
                                    colspan={6}
                                    value={t("screens.kredi_flow.total")}
                                    className="text-right bold"
                                />
                                <UI.List.Cell key={"c-6"} colspan={6} value={totalAmount} className="text-right bold" />
                            </UI.List.Row>
                        ];
                    } else {
                        listRows = [
                            <UI.List.Row key={"row-1"}>
                                <UI.List.Cell key={"c-1"} colspan={12} value={realestate} />
                            </UI.List.Row>,
                            <UI.List.Row key={"row-2"}>
                                <UI.List.Cell key={"c-2"} colspan={12} value={account} />
                            </UI.List.Row>,
                            <UI.List.Row key={"row-3"}>
                                <UI.List.Cell key={"c-3"} colspan={6} value={bookingText} />
                            </UI.List.Row>,
                            <UI.List.Row key={"row-4"}>
                                <UI.List.Cell key={"c-4"} colspan={12} value={amountCell} className="text-right" />
                            </UI.List.Row>
                        ];
                    }
                    break;
            }

            return <UI.List.Item key={`item-${accounting.id}`} screenSize={screenSize} rows={listRows} />;
        },
        [accountingAccountSearchStore, t]
    );

    const renderListHeaderRimo = useCallback(
        (screenSize: ScreenSize) => {
            if (screenSize === ScreenSize.MOBILE) {
                return <></>;
            }

            const rows = [
                <UI.List.Cell key={"c-1"} colspan={3} value={t("screens.realestate.title")} />,
                <UI.List.Cell key={"c-2"} colspan={3} value={t("screens.kredi_flow.accountings.account")} />,
                <UI.List.Cell key={"c-3"} colspan={4} value={t("screens.kredi_flow.accountings.text")} />,
                <UI.List.Cell
                    key={"c-4"}
                    colspan={2}
                    value={t("screens.kredi_flow.accountings.amount")}
                    className="text-right"
                />
            ];
            return <UI.List.Header rows={rows} />;
        },
        [t]
    );

    const accountingItems: IAccountingReadOnly[] =
        invoiceStore.currentInvoice && invoiceStore.currentInvoice.accountings
            ? invoiceStore.currentInvoice?.accountings.map((accounting) => accounting)
            : [];

    accountingItems.push({
        isTotalRow: true,
        totalAmount: invoiceStore.currentInvoice?.amount?.toFixed(2) ?? ""
    });

    const accountingDate = invoiceStore.currentInvoice?.getAccountingDate(0)
        ? getDateString(invoiceStore.currentInvoice?.getAccountingDate(0)!)
        : "";

    const realestateIT2 = invoiceStore.currentInvoice?.getAccountingRealestate(0);

    const accountingDateElement = (
        <div className="mb-4">
            <span className="bold-text">{`${t("screens.kredi_flow.accountings.date")} ${accountingDate}`}</span>
        </div>
    );

    const hasProportionalVatCode =
        invoiceStore.currentInvoice?.accountings?.some((accounting) =>
            accountingVatStore.isProportionalVatCode(accounting.vatCodeId)
        ) ?? false;

    const vatProportionHint = hasProportionalVatCode ? (
        <div className="mb-3">
            <VatProportionHint />
        </div>
    ) : undefined;

    const readOnlyViewRowsIT2 = (
        <div className="mt-4">
            {accountingDateElement}
            {vatProportionHint}
            <div className="mb-4 d-flex">
                <div className="mr-4">
                    <div className="bold-text">
                        {realestateIT2?.id ? `${t("screens.kredi_flow.accountings.mandant")}:` : ""}
                    </div>
                    <div>{realestateIT2?.name ?? ""}</div>
                    <div>{`${realestateIT2?.zip ?? ""} ${realestateIT2?.city ?? ""}`.trim()}</div>
                </div>
            </div>
            <div>
                <UI.List.BasicList
                    screenSize={uiStore.isMobile ? ScreenSize.MOBILE : ScreenSize.DESKTOP}
                    items={accountingItems ?? []}
                    renderItem={renderListRowIT2}
                    renderHeader={renderListHeaderIT2}
                />
            </div>
        </div>
    );

    const readOnlyViewRowsRimo = (
        <div className="mt-4">
            {accountingDateElement}
            {vatProportionHint}
            <div>
                <UI.List.BasicList
                    screenSize={uiStore.isMobile ? ScreenSize.MOBILE : ScreenSize.DESKTOP}
                    items={accountingItems ?? []}
                    renderItem={renderListRowRimo}
                    renderHeader={renderListHeaderRimo}
                />
            </div>
        </div>
    );

    return <>{authStore.user?.erpType === ErpType.IT2 ? readOnlyViewRowsIT2 : readOnlyViewRowsRimo}</>;
};

export const InvoiceAccountingsReadOnlyView = Sentry.withProfiler(observer(InvoiceAccoutingsReadOnlyViewBase));
